import React, { useState, useMemo } from 'react';
import CalcShell, { PrintHeaderButton } from '../CalcShell';
import { ChevronDown, ChevronUp } from 'lucide-react';

const LENGTHS = { Meter: 1, Kilometer: 1000, Centimeter: 0.01, Millimeter: 0.001, Micrometer: 1e-6, Mile: 1609.344, Yard: 0.9144, Foot: 0.3048, Inch: 0.0254 };
const WEIGHTS = { Kilogram: 1, Gram: 0.001, Milligram: 1e-6, Ton: 1000, Pound: 0.453592, Ounce: 0.0283495 };
const AREAS = { 'Square Meter': 1, 'Square Kilometer': 1e6, 'Square Centimeter': 1e-4, 'Hectare': 10000, 'Acre': 4046.86, 'Square Foot': 0.092903 };
const VOLUMES = { Liter: 1, Milliliter: 0.001, 'Cubic Meter': 1000, 'Gallon (US)': 3.78541, Quart: 0.946353, Cup: 0.24, 'Fluid Oz': 0.0295735 };

const TABS = { Length: LENGTHS, Temperature: null, Area: AREAS, Volume: VOLUMES, Weight: WEIGHTS };

export default function GasMileageCalc() {
  const [expanded, setExpanded] = useState(true);
  const [dist, setDist] = useState(300);
  const [distUnit, setDistUnit] = useState('mi');
  const [fuel, setFuel] = useState(12);
  const [cost, setCost] = useState(3.75);

  const distMi = distUnit === 'km' ? Number(dist) * 0.621371 : Number(dist);
  const mpg = fuel > 0 ? distMi / Number(fuel) : 0;
  const kml = mpg * 0.425144;
  const lp100 = kml > 0 ? 100 / kml : 0;
  const totalCost = Number(fuel) * Number(cost);
  const cpm = distMi > 0 ? totalCost / distMi : 0;

  const [tab, setTab] = useState('Length');
  const map = TABS[tab];
  const keys = map ? Object.keys(map) : ['Celsius','Fahrenheit','Kelvin'];
  const [from, setFrom] = useState(keys[0]);
  const [to, setTo] = useState(keys[1]);
  const [val, setVal] = useState(1);

  React.useEffect(() => { const k = TABS[tab] ? Object.keys(TABS[tab]) : ['Celsius','Fahrenheit','Kelvin']; setFrom(k[0]); setTo(k[1] || k[0]); }, [tab]);

  const converted = useMemo(() => {
    if (tab === 'Temperature') {
      let c = val;
      if (from === 'Fahrenheit') c = (val - 32) * 5/9;
      if (from === 'Kelvin') c = val - 273.15;
      if (to === 'Celsius') return c;
      if (to === 'Fahrenheit') return c * 9/5 + 32;
      if (to === 'Kelvin') return c + 273.15;
    }
    if (!map) return 0;
    return (val * map[from]) / map[to];
  }, [tab, from, to, val, map]);

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-2">
        <h1 className="text-4xl font-extrabold text-blue-900">Gas Mileage Calculator</h1>
        <div className="pt-1 [&_button]:!bg-slate-700 [&_button]:!text-white hover:[&_button]:!bg-slate-800"><PrintHeaderButton slug="gas-mileage"/></div>
      </div>
      <p className="text-slate-600 mb-6 max-w-3xl">Easily calculate gas mileage, fuel efficiency, total fuel cost, and trip fuel economy using your odometer readings and fuel usage. Includes a powerful built-in converter for length, temperature, area, volume, and weight units.</p>

      <button onClick={() => setExpanded(!expanded)} className="w-full rounded-2xl overflow-hidden shadow-md mb-6" style={{ background: 'linear-gradient(135deg, #1e3a8a, #1d4ed8)' }}>
        <div className="flex items-center px-6 py-5 text-white">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center mr-4">{expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}</div>
          <span className="text-xl font-bold">Modify the values and click Calculate to view results</span>
        </div>
      </button>

      {expanded && <div className="calc-card p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="field"><label>Distance</label>
            <div className="flex gap-2">
              <input type="number" value={dist} onChange={e => setDist(+e.target.value || 0)} className="flex-1"/>
              <select value={distUnit} onChange={e => setDistUnit(e.target.value)}><option value="mi">mi — mile</option><option value="km">km — kilometer</option></select>
            </div>
          </div>
          <div className="field"><label>Fuel used (gallons)</label><input type="number" step="0.01" value={fuel} onChange={e => setFuel(+e.target.value || 0)} /></div>
          <div className="field"><label>Cost per gallon ($)</label><input type="number" step="0.01" value={cost} onChange={e => setCost(+e.target.value || 0)} /></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          <div className="result-panel"><div className="result-title">MPG</div><div className="result-value text-2xl">{mpg.toFixed(2)}</div></div>
          <div className="result-panel"><div className="result-title">km/L</div><div className="result-value text-2xl">{kml.toFixed(2)}</div></div>
          <div className="result-panel"><div className="result-title">L/100 km</div><div className="result-value text-2xl">{lp100.toFixed(2)}</div></div>
          <div className="result-panel"><div className="result-title">Cost per Mile</div><div className="result-value text-2xl">${cpm.toFixed(3)}</div></div>
        </div>
      </div>}

      <div className="calc-card p-6">
        <h2 className="text-2xl font-bold mb-2">Unit Converter</h2>
        <p className="text-slate-600 mb-4">Convert between multiple measurement units instantly.</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.keys(TABS).map(t => <button key={t} onClick={() => setTab(t)} className={`px-5 py-2 rounded-full text-sm font-semibold ${tab === t ? 'bg-blue-800 text-white' : 'bg-slate-100 text-slate-700'}`}>{t}</button>)}
        </div>
        <input type="number" value={val} onChange={e => setVal(+e.target.value || 0)} className="w-full px-4 py-3 border border-slate-200 rounded-xl bg-slate-50 mb-4" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><div className="font-semibold mb-1">From</div><select value={from} onChange={e => setFrom(e.target.value)} size="8" className="w-full border border-slate-200 rounded-xl bg-slate-50 px-3 py-2">{keys.map(k => <option key={k} value={k}>{k}</option>)}</select></div>
          <div><div className="font-semibold mb-1">To</div><select value={to} onChange={e => setTo(e.target.value)} size="8" className="w-full border border-slate-200 rounded-xl bg-slate-50 px-3 py-2">{keys.map(k => <option key={k} value={k}>{k}</option>)}</select></div>
        </div>
        <div className="result-panel mt-4"><div className="result-title">Converted Result</div><div className="result-value">{isFinite(converted) ? converted.toLocaleString(undefined, { maximumFractionDigits: 6 }) : 'NaN'}</div></div>
      </div>
    </div>
  );
}
